import { RSFundPageModel } from '../../../../api/api.types';

/** Row 0 = column labels; following rows = numeric series (NAV change steps). */
export type LiquidationCostScenarioData = [
  xAxisLabels: string[],
  ...groups: number[][],
];

export type LiquidationCostData = {
  data_keys: string[][];
  [seriesKey: string]: LiquidationCostScenarioData | string[][] | unknown;
};

export type IndicativeData = {
  include_cash?: string;
  include_fx?: string;
  skip?: string | null;
  stress_level?: number;
  abs_or_percentage?: string;
  redemption_rate?: number;
  position_liquidation_delta?: string;
  num_days?: number;
  [key: string]: unknown;
};

export type VariableMarketVolumeStressTestBlock = {
  selected_position_date?: string;
  indicative_data?: IndicativeData;
  nav?: number;
  liquidation_cost_data: LiquidationCostData;
};

export type LrstKeyOption = {
  value: string;
  label: string;
};

/**
 * `RSFundPageModel.data` for the variable market volume liquidation costs page.
 */
export type VariableMarketVolumeLiquidationCostsReport = {
  stress_tests: Record<string, VariableMarketVolumeStressTestBlock>;
  lrst_keys: LrstKeyOption[];
};

export type VariableMarketVolumeLiquidationCostsPageModel =
  RSFundPageModel<VariableMarketVolumeLiquidationCostsReport>;

export function isVariableMarketVolumeLiquidationCostsReport(
  value: unknown,
): value is VariableMarketVolumeLiquidationCostsReport {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<VariableMarketVolumeLiquidationCostsReport>;
  if (!v.stress_tests || typeof v.stress_tests !== 'object') return false;
  if (!Array.isArray(v.lrst_keys)) return false;
  return true;
}

export function parseVariableMarketVolumeLiquidationCostsResponse(
  raw: unknown,
): VariableMarketVolumeLiquidationCostsReport | null {
  if (raw == null) return null;

  if (typeof raw === 'string') {
    return raw === 'data not available' ? null : null;
  }

  const candidates: unknown[] = Array.isArray(raw) ? raw : [raw];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate === 'data not available') {
      return null;
    }

    if (candidate && typeof candidate === 'object') {
      const maybeObj = candidate as Record<string, unknown>;
      if (typeof maybeObj.error === 'string' && maybeObj.error) {
        throw new Error(maybeObj.error);
      }
      if ('no data' in maybeObj) {
        return null;
      }

      if (
        'data' in maybeObj &&
        isVariableMarketVolumeLiquidationCostsReport(maybeObj.data)
      ) {
        return maybeObj.data;
      }

      if (isVariableMarketVolumeLiquidationCostsReport(candidate)) {
        return candidate;
      }
    }
  }

  return null;
}

export function getStressTestLiquidationCostData(
  report: VariableMarketVolumeLiquidationCostsReport | null | undefined,
  scenarioKey: string | null | undefined,
): LiquidationCostData | null {
  if (!report?.stress_tests || !scenarioKey) return null;
  return report.stress_tests[scenarioKey]?.liquidation_cost_data ?? null;
}
